// The question queue behind the raise, and when it is satisfied. Lifted as
// is from the Andon app's `lib/askFlow.ts`; only `sendLabel` changed shape,
// for the reason on it.
//
// ONE QUEUE, ASKED ONCE
//
// There used to be two question systems. Round 0 asked its constants one
// question per screen before the alert existed; the AI card asked its own set
// on the live screen once it did. They shared chips, shared wording, and had
// no idea about each other — so an operator answered "which pod" on the way in
// and watched the same question reappear ten seconds later under a different
// heading. `holdQuestion` existed to stop the two colliding on screen, which
// treated the symptom.
//
// The queue is one list now, bounded by what the server sends, and every
// question carries an id. Whatever is answered before the send rides with the
// raise as the ASKED REGISTER, is written onto the record as answered, and is
// handed to the model as a constraint on every later call: it may only ask
// what is not on the list. `holdQuestion` is deleted rather than tuned — there
// is nothing left to collide.
//
// AND THE ADVANCE IS GONE WITH IT
//
// The whole set is on one card, so there is no "which question are we on", no
// advance, and no `wait` branch: answering a question does not raise anything,
// pressing Send does. That removes the three-second gate the old flow needed
// while the ranked request was in flight — the ranked options now REPLACE the
// chips under the question they belong to, in place, whenever they land.
//
// These functions have no DOM in them, because the part worth testing is the
// decision.

/** Anything with a question on it — the shape both halves of the queue share. */
export interface Askable {
  question: string;
}

export interface Answered {
  question: string;
  answer: string;
}

/**
 * The whole set, in the order it is put.
 *
 * `stopIn` first when the operator picked two stars: it sets the clock the
 * board ranks two-stars by, and every other question is about what the andon
 * IS rather than when it bites. Then the constants that came down with home,
 * then whatever was ranked for this pod.
 *
 * `ranked` null means the request has not come back. That is deliberately
 * different from `[]`, which means it came back with nothing — the card says
 * "narrowing the options" for the first and nothing at all for the second.
 *
 * HOW MANY QUESTIONS THERE ARE IS A DESIGN DECISION, NOT A NUMBER HERE.
 *
 * What a tile asks is decided per tile, in `server/src/andon/questions.ts`,
 * against one test: a question earns a slot if it decides WHO IS CALLED or
 * what the first notification says. That is where the thinking belongs, it is
 * where the evidence for each question is written down, and it is the only
 * place that knows a two-star materials raise ends in the SKU while a
 * three-star equipment raise asks nothing at all.
 *
 * This constant is a RUNAWAY BACKSTOP and nothing else. It exists so a
 * misconfigured deployment, or a resolver that returns forty ranked
 * candidates, cannot put a form in front of somebody with a line stopping. It
 * is deliberately set well above what any designed path produces — the longest
 * today is four — so that hitting it means something is wrong rather than
 * meaning the design changed.
 *
 * IT WAS 3, AND 3 WAS DOING REAL DAMAGE. A two-star materials raise puts stop,
 * pod, trolley-or-part and then the SKU, and slicing at three dropped the SKU
 * — the entire objective of the tile — silently, leaving an andon that names a
 * station and a tile and nothing Stores can pick. Two-star quality was worse:
 * its fourth is "where did it come from", which IS the recipient. A cap tuned
 * to today's longest path is a cap that quietly truncates tomorrow's, which is
 * why this one is not tuned to anything.
 *
 * If this ever fires, the fix is in `questions.ts`, not here.
 */
export const MAX_QUESTIONS = 6;

export function askOrder<T extends Askable>(input: {
  stars: number;
  stopIn: T | null;
  constants: T[];
  ranked: T[] | null;
}): T[] {
  const { stars, stopIn, constants, ranked } = input;
  // Not at one star, where nothing is predicted to stop, and not at three,
  // where it already has and asking would be absurd.
  const head = stars === 2 && stopIn ? [stopIn] : [];
  return [...head, ...constants, ...(ranked ?? [])].slice(0, MAX_QUESTIONS);
}

/**
 * Whether the ranked request can be made yet.
 *
 * Every constant answered, which is the earliest moment the request is fully
 * informed. For quality and upstream that is identity; for materials it is
 * trolley-or-part as well, because which list to rank — the pod's trolleys or
 * its SKUs — depends on that answer, and firing at identity would rank the
 * wrong one.
 *
 * True with no constants at all: a scan already attached the pod, so there is
 * nothing to wait for and nothing to overlap.
 */
export function readyToRank(constants: Askable[], given: Answered[]): boolean {
  return constants.every((c) => given.some((a) => a.question === c.question));
}

/** The answer this question has, or null. Last answer wins, always. */
export function answerTo(question: string, given: Answered[]): string | null {
  return given.find((a) => a.question === question)?.answer ?? null;
}

/**
 * A question is SETTLED when it has an answer or has been skipped.
 *
 * SKIP IS BACK, AND IT IS NOT AN ANSWER. Frame v2.12 puts Skip and Don't know
 * on every question (the spec's amendment to the round-0 card). The two are
 * different facts and are recorded differently: "Don't know" rides to the
 * server as the `Not sure` answer — a person who was asked and could not
 * tell — while a skipped question rides NOWHERE. It is left off the asked
 * register, so the card asks it again on the alert a few seconds later and
 * the model is free to put it to a responder; an answer of "skipped" would
 * have told everybody downstream that the operator had been asked and had
 * said something.
 *
 * What Skip buys is the thing a mandatory gate cost: an operator at a line
 * that is stopping can get the cord pulled without being made to tap through
 * a question whose chips mean nothing to them, and the raise still carries
 * every answer they did give.
 */
export function settled(question: string, given: Answered[], skipped: readonly string[] = []): boolean {
  return answerTo(question, given) != null || skipped.includes(question);
}

/** The first question neither answered nor skipped — the one the card is asking. */
export function nextUnanswered<T extends Askable>(queue: T[], given: Answered[], skipped: readonly string[] = []): T | null {
  return queue.find((q) => !settled(q.question, given, skipped)) ?? null;
}

/**
 * WHETHER THE SEND IS LIVE.
 *
 * The gate: every question on screen is settled — answered, or skipped. The
 * answer is what chooses the responder, so a bare `★★ Materials · Steel
 * Cutting` is true of every materials andon that station has ever raised and
 * the first person notified is a coin flip. "Which station owes it" resolves
 * to a work centre and therefore to a manager; "which defect" is the
 * difference between Quality and the work-centre manager. So the questions
 * are put, one at a time, and the send counts them down — but each one can
 * be skipped as well as answered (see `settled`), because the frame says so
 * and because a gate that cannot be passed is a cord that cannot be pulled.
 *
 * IT IS BLOCKED WHILE THE RANKED HALF IS IN FLIGHT. The ranked question is
 * usually the one that matters most — the SKU on materials, the defect on
 * quality, the station that owes it on upstream — the objective of the tile,
 * and on two of those the thing that chooses the recipient. A send that goes
 * live the moment the constants are done invites exactly the raise the whole
 * of round 0 exists to prevent. Nobody skipped it on purpose; the button
 * simply lit up first. So the gate covers the whole queue, including the
 * part still arriving, and the button says it is waiting rather than
 * counting down a total it does not know yet.
 *
 * WHAT MAKES THAT SAFE IS THAT THE WAIT IS BOUNDED. `RANKED_WAIT_MS` in the
 * raise sheet gives up and treats the request as empty, which puts the card
 * back to: send live, question asked a few seconds later on the alert. A
 * stopped line can always pull the cord. That timeout is not a nicety —
 * without it a request that hangs rather than fails would strand somebody at
 * a stopped machine behind a button that never lights, which is worse than
 * anything this gate prevents.
 */
export function remaining(queue: Askable[], given: Answered[], skipped: readonly string[] = []): number {
  return queue.filter((q) => !settled(q.question, given, skipped)).length;
}

/**
 * `waiting` is the ranked request still being in flight — `ranked == null`
 * rather than `[]`. Passed in rather than inferred from the queue, because a
 * queue with nothing left to answer looks identical whether the ranked half
 * came back empty or has not come back at all, and those are opposite states.
 */
export function canSend(queue: Askable[], given: Answered[], waiting = false, skipped: readonly string[] = []): boolean {
  return !waiting && remaining(queue, given, skipped) === 0;
}

/**
 * What the raise button says — as a DECISION, not a sentence.
 *
 * The Andon app returned the English words here. The shop floor draws its
 * chrome in three languages, so this returns which of the three things the
 * button has to say and the number that goes with it, and the card turns that
 * into words through the TR map. The tests pin the decision, which is the part
 * that was ever worth pinning.
 *
 * `told` is how many people the raise will reach — the routing plus anybody the
 * operator named. It replaces "Raise it now", which was true and said nothing:
 * the whole promise of an andon is that a KNOWN NUMBER of named people are
 * coming, and a button that states the number is the cheapest possible place
 * to keep that promise visible.
 *
 * Zero is drawn as the plain label rather than "0 people told". An unrouted
 * andon is a real state and the triage board says so at length; the raise button
 * is not where somebody should discover it, and an operator with a stopped line
 * still has to be able to pull the cord.
 */
export type SendLabel =
  | { kind: 'more'; left: number }
  | { kind: 'waiting' }
  | { kind: 'raise'; told: number };

export function sendLabel(left: number, told = 0, waiting = false): SendLabel {
  // THE COUNT WINS WHILE THERE IS ANYTHING ON SCREEN TO ANSWER, and the order
  // of these three branches is the whole of it. `waiting` is true from the
  // moment the card opens until the ranked half lands, which overlaps the
  // entire time somebody is working through the constants — so checking it
  // first told an operator answering question one of three that the app was
  // waiting for something, when the thing it was waiting for was them.
  if (left > 0) return { kind: 'more', left };
  // Nothing left on screen, but a question may still be coming. Not "Raise it
  // now": a button that made the offer and then took it back when the question
  // landed is worse than one that never made it.
  //
  // "Waiting" and not "Loading" — what is being waited for is a question, and
  // the operator is about to be asked it. Loading describes the app's problem.
  if (waiting) return { kind: 'waiting' };
  return { kind: 'raise', told };
}
